import { useEffect, useRef, useState, useCallback } from "react";
import { PhoneCall, X, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const REMIND_BEFORE_MS = 10 * 60 * 1000; // 10 minutes
const GRACE_AFTER_MS = 10 * 60 * 1000;
const DISMISS_KEY = "kingofit_dismissed_reminders";

const loadDismissed = () => {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]")); }
  catch { return new Set(); }
};
const saveDismissed = (set) => {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
};

export default function CallReminders() {
  const { user } = useAuth();
  const [active, setActive] = useState([]);
  const callsRef = useRef([]);
  const dismissedRef = useRef(loadDismissed());
  const activeKeysRef = useRef(new Set());
  const snoozedRef = useRef(new Map()); // key -> timestamp until which suppressed

  const keyOf = (c) => `${c.lead_id}|${c.callback_at}`;

  const evaluate = useCallback(() => {
    const now = Date.now();
    const toAdd = [];
    for (const c of callsRef.current) {
      if (!c.callback_at) continue;
      const k = keyOf(c);
      if (dismissedRef.current.has(k) || activeKeysRef.current.has(k)) continue;
      const snoozedUntil = snoozedRef.current.get(k);
      if (snoozedUntil && now < snoozedUntil) continue;
      const ms = new Date(c.callback_at).getTime() - now;
      if (ms <= REMIND_BEFORE_MS && ms >= -GRACE_AFTER_MS) {
        activeKeysRef.current.add(k);
        toAdd.push(c);
        if ("Notification" in window && Notification.permission === "granted") {
          try {
            new Notification("Upcoming call reminder", {
              body: `${c.company_name}${c.contact_name ? " · " + c.contact_name : ""}`,
            });
          } catch { /* ignore */ }
        }
      }
    }
    if (toAdd.length) setActive((prev) => [...prev, ...toAdd]);
  }, []);

  const fetchCalls = useCallback(async () => {
    try {
      const { data } = await api.get("/leads/upcoming-calls");
      callsRef.current = data;
      evaluate();
    } catch { /* ignore */ }
  }, [evaluate]);

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    fetchCalls();
    const poll = setInterval(fetchCalls, 60 * 1000);
    const tick = setInterval(evaluate, 15 * 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [user, fetchCalls, evaluate]);

  const dismiss = (c) => {
    const k = keyOf(c);
    dismissedRef.current.add(k);
    saveDismissed(dismissedRef.current);
    setActive((prev) => prev.filter((x) => keyOf(x) !== k));
  };

  const snooze = (c) => {
    const k = keyOf(c);
    snoozedRef.current.set(k, Date.now() + 5 * 60 * 1000);
    activeKeysRef.current.delete(k);
    setActive((prev) => prev.filter((x) => keyOf(x) !== k));
  };

  const openLead = (c) => {
    dismiss(c);
    window.open(`/app/leads/${c.lead_id}`, "_blank", "noopener");
  };

  const relTime = (iso) => {
    const ms = new Date(iso).getTime() - Date.now();
    const mins = Math.round(ms / 60000);
    if (mins <= 0) return "now";
    if (mins === 1) return "in 1 minute";
    return `in ${mins} minutes`;
  };

  if (!user || typeof user !== "object" || active.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3 w-[340px] max-w-[calc(100vw-2rem)]" data-testid="call-reminders">
      {active.map((c) => (
        <div
          key={keyOf(c)}
          data-testid={`call-reminder-${c.lead_id}`}
          className="rounded-xl border border-amber-500/40 bg-[#131C31] shadow-2xl shadow-amber-500/10 p-4 animate-fade-up"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                <PhoneCall className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <p className="font-display font-bold text-sm text-white">Call Reminder</p>
                <p className="text-[11px] text-amber-400 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Call {relTime(c.callback_at)}
                </p>
              </div>
            </div>
            <button data-testid={`dismiss-reminder-${c.lead_id}`} onClick={() => dismiss(c)} className="text-slate-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="font-semibold text-white mt-2.5">{c.company_name}</p>
          {c.contact_name && <p className="text-xs text-slate-400">{c.contact_name}</p>}
          {c.note && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{c.note}</p>}
          <div className="flex gap-2 mt-3">
            <button onClick={() => openLead(c)} className="btn-primary rounded-lg py-1.5 px-3 text-xs flex-1">
              Open Lead
            </button>
            <button data-testid={`snooze-reminder-${c.lead_id}`} onClick={() => snooze(c)} className="bg-slate-800 text-white text-xs font-semibold py-1.5 px-3 rounded-lg border border-slate-700 hover:bg-slate-700">
              Snooze 5m
            </button>
            <button onClick={() => dismiss(c)} className="bg-slate-800 text-white text-xs font-semibold py-1.5 px-3 rounded-lg border border-slate-700 hover:bg-slate-700">
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
